// The same save pressed twice records once. (ZEN-272, ZEN-758)
//
// A form makes ONE key when it opens and sends it with every press of Save in
// that opening. The server (backend/save_once.py) answers a key it has already
// used with the record the first save made, instead of creating a second one.
// A genuine second record comes from a fresh opening, which has its own key.
//
// The key is never made from what was typed: two identical expenses are
// allowed, and only the key says whether a save is a repeat.

import { useState } from "react";

export const SAVE_KEY_HEADER = "Idempotency-Key";

// A new random key, or null where the browser cannot make one — in which case
// the save goes out with no key and behaves as it did before this change.
export function newSaveKey(source = globalThis.crypto) {
  if (typeof source?.randomUUID === "function") return source.randomUUID();
  if (typeof source?.getRandomValues === "function") {
    const bytes = source.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return null;
}

// The request options that carry the key: `api.post(url, body, withSaveKey(key))`.
export function withSaveKey(key) {
  return key ? { headers: { [SAVE_KEY_HEADER]: key } } : {};
}

// The key for one opening of a form. `opening` is any string that changes
// when the form opens for something new (for example `${open}:${id || "new"}`).
//
// Held in STATE and replaced while drawing when the opening changes (React's
// own pattern for "reset when a prop changes"), so the form on screen always
// holds the key of its own opening. An effect would set it a moment after the
// form appears; a ref written while drawing can be changed by a drawing React
// throws away.
export function useSaveKey(opening) {
  const [held, setHeld] = useState(() => ({ opening, key: newSaveKey() }));
  if (held.opening !== opening) setHeld({ opening, key: newSaveKey() });
  return held.opening === opening ? held.key : null;
}
